<template>
  <div class="app-menu-mobile">
    <AppMenuMobileMenu :menu="menu" />
  </div>
</template>

<script setup lang="ts">
import type { MenuData } from '~/queries'
import IconFavourite from './IconFavourite.vue'
import IconLogout from './IconLogout.vue'
import IconPassword from './IconPassword.vue'
import IconTicket from './IconTicket.vue'
import IconUser from './IconUser.vue'
import { useSiteLanguages } from '~/composables/useSiteLanguages'

const { locale, t } = useI18n()
const { site } = useSite()

const auth = useAuthStore()
const menus = useMenusStore()
const modals = useModalsStore()
const currency = useCurrencyStore()
const { languages } = useSiteLanguages()
const hasCurrencySelector = computed(() => (currency.list?.length ?? 0) > 1)
const emit = defineEmits<{ (event: 'open-region'): void }>()

const initials = computed(() => {
  if (!auth.user) return undefined

  const { firstName, lastName } = auth.user

  return `${firstName[0]}${lastName[0]}`
})

function menuWrapChildren(menu: MenuData): any {
  return menu.map((item) => ({
    ...item,
    children: item.children ? [menuWrapChildren(item.children)] : undefined,
  }))
}

const menu = computed(() => {
  return [
    [
      auth.isAuthenticated
        ? {
            title: t('string.Your-account'),
            initials: initials.value,
            children: [
              [
                {
                  title: t('string.Personal-details'),
                  url: '/account/personal-details',
                  icon: IconUser,
                },
                {
                  title: t('string.Password'),
                  url: '/account/password',
                  icon: IconPassword,
                },
                {
                  title: t('string.Tickets'),
                  url: '/account/tickets',
                  icon: IconTicket,
                },
                {
                  title: t('string.Favourites'),
                  url: '/account/favourites',
                  icon: IconFavourite,
                },
                {
                  title: t('string.Logout'),
                  icon: IconLogout,
                  action: auth.logout,
                },
              ],
            ],
          }
        : {
            title: `${t('string.Login')} / ${t('string.Register')}`,
            icon: IconUser,
            action: modals.openLoginModal,
          },
    ],
    menus.main ? menuWrapChildren(menus.main) : [],
    [
      { title: t('string.About'), url: '/about-us' },
      { title: 'FAQs', url: '/faqs' },
      {
        title: hasCurrencySelector.value
          ? `${t('string.Language')} / ${t('string.Currency')}: ${currency.selected?.code ?? ''}`
          : t('string.Change-language'),
        flag: {
          src: `/images/flags/${locale.value}.png`,
          alt: site.value.name,
        },
        action: hasCurrencySelector.value
          ? () => emit('open-region')
          : undefined,
        children: hasCurrencySelector.value
          ? undefined
          : [
              languages.value.map((language) => ({
                title: language.name,
                url: language.link,
                flag: {
                  src: `/images/flags/${language.code}.png`,
                  alt: language.name,
                },
              })),
            ],
      },
    ],
  ]
})
</script>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-menu-mobile {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  height: 100dvh;
  padding-top: var(--navbar-offset-height, 3.5rem);
  overflow: hidden;
  overscroll-behavior: none;
  color: $black;
  background-color: $white;
  z-index: 990;
}
</style>
